import { useMemo } from "react";
import dynamic from "next/dynamic";
import { useMedia } from "@remi/next-ui/hooks";

import { useViewportBounds } from "~/components/popout/useViewportBounds";
import {
  type PopoutContent,
  type PopoutSlot,
  type PopoutSlotOfKind,
  usePopoutManager,
} from "~/contexts/PopoutManagerContext";

const ConversationPopout = dynamic(() =>
  // biome-ignore lint/plugin/no-async-import: defer popout chunk (RemiDialog + channel feed components) from _app bundle
  import("~/components/conversations/popout/ConversationPopout").then((m) => m.ConversationPopout),
);

const TicketPopout = dynamic(() =>
  // biome-ignore lint/plugin/no-async-import: defer popout chunk (RemiDialog + ticket body components) from _app bundle
  import("~/components/tickets/popout/TicketPopout").then((m) => m.TicketPopout),
);

const TaskPopout = dynamic(() =>
  // biome-ignore lint/plugin/no-async-import: defer popout chunk (RemiDialog + task body components) from _app bundle
  import("~/components/tasks/popout/TaskPopout").then((m) => m.TaskPopout),
);

const MinimizedPopoutPillList = dynamic(() =>
  // biome-ignore lint/plugin/no-async-import: defer popout chunk (RemiDialog + channel feed components) from _app bundle
  import("~/components/conversations/popout/MinimizedPopoutPillList").then((m) => m.MinimizedPopoutPillList),
);

const isSlotOfKind = <K extends PopoutContent["kind"]>(slot: PopoutSlot, kind: K): slot is PopoutSlotOfKind<K> =>
  slot.content.kind === kind;

// Rendered deep in the provider tree (inside the contact/agreement drawer providers) so popouts
// can reach those contexts — e.g. contact-card buttons that open the contact drawer.
export const PopoutViewport = () => {
  const { state } = usePopoutManager();
  const bounds = useViewportBounds();
  const { isMobile } = useMedia();

  const conversationSlots = useMemo(
    (): PopoutSlotOfKind<"conversation">[] => state.popouts.filter((p) => isSlotOfKind(p, "conversation")),
    [state.popouts],
  );
  const ticketSlots = useMemo(
    (): PopoutSlotOfKind<"ticket">[] => state.popouts.filter((p) => isSlotOfKind(p, "ticket")),
    [state.popouts],
  );
  const taskSlots = useMemo(
    (): PopoutSlotOfKind<"task">[] => state.popouts.filter((p) => isSlotOfKind(p, "task")),
    [state.popouts],
  );
  const minimizedSlots = useMemo(() => state.popouts.filter((p) => p.status === "minimized"), [state.popouts]);

  const topMostOpen = useMemo(
    () =>
      state.popouts
        .filter((p) => p.status === "open")
        .reduce<PopoutSlot | null>((top, p) => (top === null || p.zIndex > top.zIndex ? p : top), null),
    [state.popouts],
  );

  if (isMobile) {
    if (topMostOpen !== null) {
      if (isSlotOfKind(topMostOpen, "conversation")) {
        return <ConversationPopout key={topMostOpen.popoutId} popout={topMostOpen} bounds={bounds} />;
      }
      if (isSlotOfKind(topMostOpen, "ticket")) {
        return <TicketPopout key={topMostOpen.popoutId} popout={topMostOpen} bounds={bounds} />;
      }
      if (isSlotOfKind(topMostOpen, "task")) {
        return <TaskPopout key={topMostOpen.popoutId} popout={topMostOpen} bounds={bounds} />;
      }
    }
    return minimizedSlots.length > 0 ? <MinimizedPopoutPillList popouts={minimizedSlots} /> : null;
  }

  return (
    <>
      {/* Key includes initialOffset.x so a popout whose cascade slot shifted (after CLOSE
          or eviction) re-mounts at the new position. Popouts whose offset didn't change
          keep the same key and retain internal state (drag position, scroll, etc.). */}
      {conversationSlots.map((p) => (
        <ConversationPopout
          key={`${p.popoutId}:${p.initialOffset.x}`}
          popout={p}
          bounds={bounds}
          isHidden={p.status === "minimized"}
        />
      ))}
      {ticketSlots.map((p) => (
        <TicketPopout
          key={`${p.popoutId}:${p.initialOffset.x}`}
          popout={p}
          bounds={bounds}
          isHidden={p.status === "minimized"}
        />
      ))}
      {taskSlots.map((p) => (
        <TaskPopout
          key={`${p.popoutId}:${p.initialOffset.x}`}
          popout={p}
          bounds={bounds}
          isHidden={p.status === "minimized"}
        />
      ))}
      {minimizedSlots.length > 0 ? <MinimizedPopoutPillList popouts={minimizedSlots} /> : null}
    </>
  );
};
